import styled from 'styled-components';
import { translations } from '../data/i18n';
import { siteConfig } from '../data/site';

const Page = styled.main`
  padding: calc(var(--nav-height) + 70px) 28px 90px;
  background:
    radial-gradient(circle at top right, rgba(221,161,47,0.12), transparent 34%),
    var(--cream);
  min-height: calc(100vh - var(--nav-height));
`;

const Card = styled.div`
  max-width: 980px;
  margin: 0 auto;
  background: var(--cream-card);
  border-radius: 26px;
  padding: clamp(28px, 4vw, 52px);
  box-shadow: var(--shadow);
  border: 1px solid rgba(221,161,47,0.12);
`;

const Eyebrow = styled.div`
  color: var(--gold-dark);
  font-weight: 950;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  margin: 0 0 18px;
  color: var(--black);
  font-size: clamp(2.4rem, 5vw, 4.3rem);
  line-height: 0.95;
  letter-spacing: -2px;
  text-transform: uppercase;
`;

const Intro = styled.p`
  max-width: 720px;
  margin: 0 0 34px;
  color: var(--muted);
  line-height: 1.75;
  font-size: 1.05rem;
`;

const Section = styled.section`
  margin-top: 34px;

  h2 {
    margin: 0 0 18px;
    color: var(--black);
    font-size: 1.45rem;
    text-transform: uppercase;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 0;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 18px;
  overflow: hidden;
  background: #fff;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.div`
  padding: 16px 18px;
  background: rgba(221,161,47,0.08);
  border-bottom: 1px solid rgba(0,0,0,0.07);
  font-weight: 950;
`;

const Value = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid rgba(0,0,0,0.07);
  color: var(--muted);
  line-height: 1.65;

  a {
    color: var(--gold-dark);
    font-weight: 900;
  }
`;

function Impressum({ language = 'de' }) {
  const t = translations[language]?.legal || translations.de.legal;
  const footer = translations[language]?.footer || translations.de.footer;

  const { legal, address, credits } = siteConfig;
  const isEnglish = language === 'en';

  return (
    <Page>
      <Card>
        <Eyebrow>{siteConfig.brandName}</Eyebrow>
        <Title>{t.imprintTitle || footer.imprint}</Title>

        <Intro>
          {isEnglish
            ? 'Legal information according to Austrian disclosure obligations. The following data refers to the business owner and the trade licence relevant to this website.'
            : 'Angaben gemäß österreichischer Offenlegungspflichten. Die folgenden Daten beziehen sich auf die Unternehmerin und die für diese Website relevante Gewerbeberechtigung.'}
        </Intro>

        <Section>
          <h2>{isEnglish ? 'Business owner' : 'Unternehmerin / Medieninhaberin'}</h2>

          <Grid>
            <Label>{isEnglish ? 'Name' : 'Name'}</Label>
            <Value>{legal.owner}</Value>

            <Label>{isEnglish ? 'Business name' : 'Firmenname'}</Label>
            <Value>{legal.companyName}</Value>

            <Label>GLN</Label>
            <Value>{legal.gln}</Value>

            <Label>{isEnglish ? 'Website brand' : 'Website / Marke'}</Label>
            <Value>
              {siteConfig.brandName} – {siteConfig.tagline}
            </Value>
          </Grid>
        </Section>

        <Section>
          <h2>{isEnglish ? 'Contact' : 'Kontakt'}</h2>

          <Grid>
            <Label>{isEnglish ? 'Business/contact address' : 'Betriebs-/Kontaktadresse'}</Label>
            <Value>
              {address.street}<br />
              {address.zip} {address.city}<br />
              {address.country}<br />
              <a href={address.mapsUrl} target="_blank" rel="noreferrer">
                {isEnglish ? 'Plan route' : 'Route planen'}
              </a>
            </Value>

            <Label>{isEnglish ? 'Phone' : 'Telefon'}</Label>
            <Value>
              <a href={`tel:${siteConfig.phoneHref}`}>
                {siteConfig.phoneDisplay}
              </a>
            </Value>

            <Label>E-Mail</Label>
            <Value>
              <a href={`mailto:${siteConfig.email}`}>
                {siteConfig.email}
              </a>
            </Value>
          </Grid>
        </Section>

        <Section>
          <h2>{isEnglish ? 'Trade licence' : 'Gewerbeberechtigung'}</h2>

          <Grid>
            <Label>{isEnglish ? 'Trade category' : 'Berechtigung'}</Label>
            <Value>{legal.trade.title}</Value>

            <Label>{isEnglish ? 'Trade wording' : 'Gewerbewortlaut'}</Label>
            <Value>{legal.trade.wording}</Value>

            <Label>GISA-Zahl</Label>
            <Value>{legal.trade.gisa}</Value>

            <Label>{isEnglish ? 'Business sector' : 'Berufszweig'}</Label>
            <Value>{legal.trade.branch}</Value>

            <Label>{isEnglish ? 'Responsible authority' : 'Behörde gemäß ECG'}</Label>
            <Value>{legal.authority}</Value>

            <Label>{isEnglish ? 'Trade address according to GISA' : 'Gewerbestandort laut GISA'}</Label>
            <Value>
              {legal.businessAddress.street}<br />
              {legal.businessAddress.zip} {legal.businessAddress.city}<br />
              {legal.businessAddress.country}
            </Value>

            <Label>{isEnglish ? 'Since' : 'Seit'}</Label>
            <Value>{legal.trade.since}</Value>
          </Grid>
        </Section>

        <Section>
          <h2>{isEnglish ? credits.titleEn : credits.titleDe}</h2>

          <Grid>
            <Label>{isEnglish ? 'Implementation' : 'Umsetzung'}</Label>
            <Value>
              {credits.company}<br />
              {credits.name}<br />
              <a href={credits.website} target="_blank" rel="noreferrer">
                {credits.website}
              </a>
            </Value>
          </Grid>
        </Section>

        <Section>
          <h2>{isEnglish ? 'Disclaimer' : 'Haftungshinweis'}</h2>

          <Grid>
            <Label>{isEnglish ? 'Content' : 'Inhalte'}</Label>
            <Value>
              {isEnglish
                ? 'The contents of this website are created with care. No guarantee is given for completeness, correctness or currentness.'
                : 'Die Inhalte dieser Website werden mit größtmöglicher Sorgfalt erstellt. Für Vollständigkeit, Richtigkeit und Aktualität wird keine Gewähr übernommen.'}
            </Value>

            <Label>{isEnglish ? 'External links' : 'Externe Links'}</Label>
            <Value>
              {isEnglish
                ? 'This website may contain links to external websites. Responsibility for their content lies with the respective operators.'
                : 'Diese Website kann Links zu externen Websites enthalten. Für deren Inhalte sind ausschließlich die jeweiligen Betreiber verantwortlich.'}
            </Value>
          </Grid>
        </Section>
      </Card>
    </Page>
  );
}

export default Impressum;
